import * as React from 'react'
import { ListBlock } from '@basehub/data/replicache/index'
import { useDroppable } from '@dnd-kit/core'
import {
  listBlockDndControllers,
  DroppableZone,
  DropIndicator,
  useDragStore,
  DroppableZoneProps,
} from '../../dnd/index'
import { useTableDropAssets } from '../../dnd/use-table-drop-assets'
import isEqual from 'lodash.isequal'

export const DroppableTopZone: React.FC<DroppableZoneProps> = React.memo(
  ({ parentBlock, parentPath, index, id, style, onDropFilesCallback }) => {
    const setActiveZoneId = useDragStore((state) => state.setActiveZoneId)
    const onDrop = useTableDropAssets({
      listBlock: parentBlock,
      path: parentPath,
    })
    const zoneId = `list-entry-droppable-top/${id}`
    const droppable = useDroppable({
      id: zoneId,
      data: listBlockDndControllers.droppableDataSchema.parse({
        intendedIndex: index,
        type: listBlockDndControllers.dndType,
      }),
      disabled: !id,
    })

    const isValid = React.useMemo(() => {
      if (!droppable.active || !droppable.isOver || !droppable.over) {
        return false
      }
      const parsed = listBlockDndControllers.draggableDataSchema.safeParse(
        droppable.active.data.current
      )

      if (!parsed.success) return false

      const activeItemData = parsed.data

      return isValidDnd({
        intendedIndex: index,
        dragItemIndex: activeItemData.index,
        isDraggingTo: parentBlock.id,
        parentBlockId: activeItemData.shouldDropTo,
      })
    }, [droppable.active, droppable.isOver, droppable.over, index, parentBlock])

    React.useEffect(() => {
      if (droppable.isOver) {
        setActiveZoneId(zoneId)
      }
    }, [droppable.isOver, setActiveZoneId, zoneId])

    const alignTopClassName =
      "pointer-events-none z-50 absolute -left-12 w-[calc(100%+theme(spacing.12))] after:content-[''] after:w-full after:absolute after:-top-px after:left-12 after:h-0.5 after:rounded-full after:bg-dark-control-base"

    return (
      <>
        <span
          ref={droppable.setNodeRef}
          style={style}
          className="pointer-events-none z-50 absolute -left-12 w-[calc(100%+theme(spacing.12))]"
        />
        <DropIndicator
          style={style}
          isVisible={isValid}
          className={alignTopClassName}
        />
        <DroppableZone
          style={style}
          id={id}
          zoneId={zoneId}
          onDrop={onDrop}
          onDropFilesCallback={onDropFilesCallback}
          className="absolute -left-12 overflow-visible z-50"
          indicatorClassName={alignTopClassName}
        />
      </>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.index === nextProps.index &&
      prevProps.id === nextProps.id &&
      prevProps.parentPath === nextProps.parentPath &&
      isEqual(prevProps.style, nextProps.style) &&
      prevProps.onDropFilesCallback === nextProps.onDropFilesCallback
    )
  }
)

export const DroppableBottomZone: React.FC<DroppableZoneProps> = React.memo(
  ({ parentBlock, parentPath, index, id, style, onDropFilesCallback }) => {
    const intendedIndex = index + 1
    const setActiveZoneId = useDragStore((state) => state.setActiveZoneId)
    const onDrop = useTableDropAssets({
      listBlock: parentBlock,
      path: parentPath,
    })
    const zoneId = `list-entry-droppable-bottom/${id}`
    const droppable = useDroppable({
      id: zoneId,
      data: listBlockDndControllers.droppableDataSchema.parse({
        intendedIndex,
        type: listBlockDndControllers.dndType,
      }),
      disabled: !id,
    })

    const isValid = React.useMemo(() => {
      if (!droppable.active || !droppable.isOver || !droppable.over) {
        return false
      }
      const parsed = listBlockDndControllers.draggableDataSchema.safeParse(
        droppable.active.data.current
      )

      if (!parsed.success) return false

      const activeItemData = parsed.data

      return isValidDnd({
        intendedIndex: index,
        dragItemIndex: activeItemData.index,
        isDraggingTo: parentBlock.id,
        parentBlockId: activeItemData.shouldDropTo,
      })
    }, [
      droppable.active,
      droppable.isOver,
      droppable.over,
      index,
      parentBlock.id,
    ])

    React.useEffect(() => {
      if (droppable.isOver) {
        setActiveZoneId(zoneId)
      }
    }, [droppable.isOver, setActiveZoneId, zoneId])

    const alignBottomClassName =
      "pointer-events-none z-50 absolute -left-12 w-[calc(100%+theme(spacing.12))] after:content-[''] after:w-full after:absolute after:-bottom-px after:left-12 after:h-0.5 after:rounded-full after:bg-dark-control-base"

    return (
      <>
        <span
          ref={droppable.setNodeRef}
          style={style}
          className="pointer-events-none z-50 absolute -left-12 w-[calc(100%+theme(spacing.12))]"
        />
        <DropIndicator
          style={style}
          isVisible={isValid}
          className={alignBottomClassName}
        />
        <DroppableZone
          style={style}
          id={id}
          zoneId={zoneId}
          onDrop={onDrop}
          onDropFilesCallback={onDropFilesCallback}
          indicatorClassName={alignBottomClassName}
          className="absolute -left-12 overflow-visible z-50"
        />
      </>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.index === nextProps.index &&
      prevProps.id === nextProps.id &&
      prevProps.parentPath === nextProps.parentPath &&
      isEqual(prevProps.style, nextProps.style) &&
      prevProps.onDropFilesCallback === nextProps.onDropFilesCallback
    )
  }
)

export function isValidDnd({
  intendedIndex,
  dragItemIndex,
  isDraggingTo,
  parentBlockId,
}: {
  intendedIndex: number
  dragItemIndex: number
  isDraggingTo: ListBlock['id']
  parentBlockId: ListBlock['id']
}) {
  if (parentBlockId !== isDraggingTo) return false

  const isDraggingToSamePosition =
    // same index
    intendedIndex === dragItemIndex ||
    // also same index, cause this will be re-adjusted afterwards
    intendedIndex === dragItemIndex + 1

  if (isDraggingToSamePosition) return false

  return true
}
